import { memo, useEffect, useState } from "react";
import { Dialog, DialogContent, Grid2, Skeleton, Chip, Card, Typography, IconButton, LinearProgress, Tooltip, DialogTitle, DialogActions, Button, Box } from "@mui/material";
import Markdown from "react-markdown";
import { useQuery, useMutation } from "@apollo/client";
import { Query, Mutation, ContentNode, ContentNodeType } from "@/app/graphql/codegen";
import { FETCH_CONTEXT_SELECTION_FOR_CHAT } from "@chat/graphql/queries/fetchContextSelectionForChat";
import { FETCH_CONTEXT_SIZES_FOR_CHAT } from "@chat/graphql/queries/fetchContextSizesForChat";
import { FETCH_GRAPH_MODE_INFO_FOR_CHAT } from "@chat/graphql/queries/fetchGraphModeInfoForChat";
import { CLEAR_CONTEXT_FOR_CHAT } from "@chat/graphql/mutations/clearContextForChat";
import { REMOVE_ATTACHMENT } from "@chat/graphql/mutations/removeAttachment";
import { icon } from "@/app/style/icons";
import DocChip from "@chat/components/DocChip";
import DocumentDialog from "@chat/components/DocumentDialog";
import { useSelectContentNodeInChat } from "@chat/hooks/useSelectContentNodeInChat";
import { useEnterKeyDialog } from "@core/hooks/useEnterKeyDialog";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/app/store";
import { clearInputValue } from "@store/slices/chat";
import { FETCH_USER_PROFILE } from "@company/graphql/queries/fetchUserProfile";
import { isDocumentVisible } from "@/utils/documentDisplay";
import { triggerNotification } from "@store/slices/notification";

const COST_FOR_EMPTY = 1;

interface SelectionBoardProps {
    chatId: string;
}

const SelectionBoard = memo(({ chatId }: SelectionBoardProps) => {
    const dispatch = useDispatch();
    const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
    const [selectedNode, setSelectedNode] = useState<ContentNode | null>(null);
    const [loadSizes, setLoadSizes] = useState(false);

    // Fetch lightweight context data (selection/modes/attachments)
    const {
        data: selectionData,
        loading: isLoading,
        error,
    } = useQuery(FETCH_CONTEXT_SELECTION_FOR_CHAT, {
        variables: { chatId },
        skip: !chatId,
        pollInterval: 10000,
    });

    // Defer expensive size computations until after first paint to avoid blocking initial UI.
    useEffect(() => {
        if (!chatId) return;
        const t = window.setTimeout(() => setLoadSizes(true), 250);
        return () => window.clearTimeout(t);
    }, [chatId]);

    const { data: sizesData } = useQuery(FETCH_CONTEXT_SIZES_FOR_CHAT, {
        variables: { chatId },
        skip: !chatId || !loadSizes,
        pollInterval: 10000,
    });

    // Load user profile to know visibility settings
    const { data: userProfileData } = useQuery<Pick<Query, "userProfile">>(FETCH_USER_PROFILE);
    const userSettings = userProfileData?.userProfile?.chatSettings || {};
    const showSummaries = userSettings.showSummaries === true;
    const showOriginals = userSettings.showOriginals === true; // default false
    const showAmendments = userSettings.showAmendments === true; // default false

    const [clearContext, { loading: isClearing }] = useMutation(CLEAR_CONTEXT_FOR_CHAT);
    const [removeAttachment] = useMutation(REMOVE_ATTACHMENT);

    const { selectContentNode } = useSelectContentNodeInChat(chatId);

    const inputValue = useSelector((state: RootState) => state.chat.inputValues[chatId] || "");

    // Merge selection + sizes (both share the same Context.id so Apollo cache will also merge)
    const context = (selectionData?.contextForChat || sizesData?.contextForChat) as any;
    const sizeInPages = sizesData?.contextForChat?.sizeInPages ?? 0;
    const totalAllowedPages = sizesData?.contextForChat?.totalAllowedPages ?? context?.totalAllowedPages ?? 0;

    // Graph-mode info is expensive compared to base context; fetch it only when graph mode is enabled.
    const { data: graphModeInfoData } = useQuery<{
        graphModeCostEstimate: number | null;
        graphModeVariant: string | null;
    }>(FETCH_GRAPH_MODE_INFO_FOR_CHAT, {
        variables: { chatId },
        skip: !chatId || !context?.graphEnabled,
    });

    const graphModeCostEstimate = graphModeInfoData?.graphModeCostEstimate ?? COST_FOR_EMPTY;
    const graphVariant = graphModeInfoData?.graphModeVariant ?? undefined;

    useEffect(() => {
        if (context?.graphEnabled && graphVariant) {
            console.log(`SmartSearch variant: ${graphVariant}`);
        }
    }, [graphVariant, context?.graphEnabled]);

    const isGptOnly = (context as any)?.gptOnlyEnabled === true;
    const selectedContentNodes = isGptOnly ? [] : (context?.contentNodes?.filter((node: any): node is ContentNode => node !== null) || []);
    
    // Get attachments from context
    const attachments = (context as any)?.attachments || [];
    
    // Check if we should show "fake" GPT mode when no documents are selected
    const hasNoDocuments = selectedContentNodes.length === 0 && !context?.graphEnabled && !context?.companyContextEnabled && attachments.length === 0;
    const isEffectiveGptMode = isGptOnly || hasNoDocuments;

    // Helper to decide if node is currently hidden in tree under user settings
    const isNodeHidden = (node: ContentNode): boolean => {
        // Summary visibility handling
        if (node.type === ContentNodeType.DetailedSummary) {
            // Detailed summaries are always hidden
            return true;
        }

        if (node.type === ContentNodeType.GraphSummary) {
            // Graph summaries visibility depends on user setting
            return !showSummaries;
        }

        // Apply document visibility rules
        const doc = node.associatedOfficialDocument as any;
        if (doc) {
            return !isDocumentVisible(doc, { showOriginals, showAmendments });
        }
        return false;
    };

    // ---------- Context usage calculation ----------
    // Calculate attachment pages
    const attachmentPages = attachments.reduce((sum: number, att: any) => sum + (att.pages || 0), 0);
    
    // Base cost calculation depends on mode
    let baseCost: number;
    let usedPages: number;
    
    if (isEffectiveGptMode) {
        // GPT mode always uses minimal cost
        baseCost = COST_FOR_EMPTY;
        const pagesForMessage = Math.ceil(Math.max(0, inputValue.length) / 3200);
        usedPages = baseCost + pagesForMessage + attachmentPages;
    } else {
        // Normal mode calculation
        baseCost = context?.graphEnabled ? graphModeCostEstimate : COST_FOR_EMPTY;
        const pagesForMessage = Math.ceil(Math.max(0, inputValue.length) / 3200);
        usedPages = (context?.graphEnabled ? 0 : sizeInPages) + baseCost + pagesForMessage + attachmentPages;
    }

    const progressPercentage = totalAllowedPages ? (usedPages / totalAllowedPages) * 100 : 0;
    const isOverflow = totalAllowedPages ? usedPages > totalAllowedPages : false;

    const handleSelectDocument = (node: ContentNode) => {
        setSelectedNode(node);
        setDocumentDialogOpen(true);
    };

    const handleNodeToggle = (nodeId: string, select: boolean) => {
        // Beim entfernen eines Gesetzes, soll der Input Text entfernt werden, wenn er einem bestimmten Wert entspricht
        if (inputValue === "Bitte beantworte meine Frage mit dem angewählten Dokument.") {
            dispatch(clearInputValue(chatId));
        }

        selectContentNode(nodeId, select);
    };

    const handleClearAll = () => {
        clearContext({
            variables: { chatId },
            refetchQueries: [
                { query: FETCH_CONTEXT_SELECTION_FOR_CHAT, variables: { chatId } },
                { query: FETCH_CONTEXT_SIZES_FOR_CHAT, variables: { chatId } },
            ],
        });
    };

    const handleRemoveAttachment = async (attachmentId: string) => {
        try {
            await removeAttachment({
                variables: { attachmentId },
                refetchQueries: [
                    { query: FETCH_CONTEXT_SELECTION_FOR_CHAT, variables: { chatId } },
                    { query: FETCH_CONTEXT_SIZES_FOR_CHAT, variables: { chatId } },
                ],
            });
            dispatch(triggerNotification({ 
                content: "Anhang entfernt", 
                type: "success", 
                autoHideDuration: 2000 
            }));
        } catch (error) {
            console.error("Error removing attachment:", error);
            dispatch(triggerNotification({ 
                content: "Fehler beim Entfernen des Anhangs", 
                type: "error", 
                autoHideDuration: 3000 
            }));
        }
    };

    useEnterKeyDialog({
        isOpen: documentDialogOpen,
        onConfirm: () => setDocumentDialogOpen(false),
    });

    if (error) {
        console.error("Error loading context:", error);
    }

    if (isLoading) {
        return (
            <Grid2 container size={12} spacing={1}>
                {[1, 2, 3].map((i) => (
                    <Grid2 key={i}>
                        <Skeleton width={100} height={40} />
                    </Grid2>
                ))}
            </Grid2>
        );
    }

    const selectionExists = selectedContentNodes.length > 0;

    return (
        <Grid2 size={12} p={1}>
            <Card>
                <Card>
                    <Grid2 container size={12} py={1} px={1.5} spacing={0.5}>
                        {isEffectiveGptMode ? (
                            // Minimal display for GPT mode (real or fake)
                            <>
                                <Grid2 display="flex" flexDirection="row" justifyContent="space-between" size={12}>
                                    <Typography fontSize={13}>
                                        Standard KI Modus ist aktiv - Anfragen werden ohne Dokument-Kontext verarbeitet
                                    </Typography>
                                    <Typography
                                        fontSize={12}
                                        fontStyle={"italic"}
                                    >{`(geschätzte Seitenkosten: ${COST_FOR_EMPTY})`}</Typography>
                                </Grid2>
                            </>
                        ) : (
                            // Normal display for document/graph modes  
                            <>
                                <Grid2 display="flex" flexDirection="row" justifyContent="space-between" size={12}>
                                    <Typography fontSize={13}>{context?.graphEnabled ? "Graphauswahl aktiv" : "Ausgewählte Dokumente für Deine nächste Frage: "}</Typography>
                                    <Typography
                                        fontSize={12}
                                        fontStyle={"italic"}
                                    >{`(geschätzte Seitenkosten: ${(context?.sizeInPages ?? 0) + (context?.graphEnabled ? graphModeCostEstimate : COST_FOR_EMPTY) + attachmentPages})`}</Typography>
                                </Grid2>

                                <Grid2 container size={12} alignItems={"center"}>
                                    <Grid2 container size={"grow"} spacing={1}>
                                        {context?.graphEnabled && (
                                            <Grid2>
                                                <DocChip chipType="graph" size="small" truncateLabel={true} showTooltip={true} />
                                            </Grid2>
                                        )}

                                        {context?.companyContextEnabled && (
                                            <Grid2>
                                                <DocChip chipType="company" size="small" truncateLabel={true} showTooltip={true} />
                                            </Grid2>
                                        )}

                                        {!context?.graphEnabled &&
                                            selectionExists &&
                                            selectedContentNodes.map((node: ContentNode) => {
                                                const hidden = isNodeHidden(node);
                                                return (
                                                    <Grid2 key={node.id}>
                                                        <DocChip
                                                            node={node}
                                                            size="small"
                                                            onClick={() => setSelectedNode(node)}
                                                            onDelete={() => handleNodeToggle(node.id, false)}
                                                            truncateLabel={true}
                                                            showTooltip={true}
                                                            tooltipTitle={hidden ? "aktuell ausgeblendet" : undefined}
                                                            dimmed={hidden}
                                                        />
                                                    </Grid2>
                                                );
                                            })}

                                        {attachments.length > 0 &&
                                            attachments.map((attachment: any) => (
                                                <Grid2 key={attachment.id || attachment.messageId}>
                                                    <DocChip
                                                        chipType="attachment"
                                                        size="small"
                                                        fileName={attachment.fileName}
                                                        fileSize={attachment.fileSize}
                                                        fileTokens={attachment.tokens}
                                                        truncateLabel={true}
                                                        showTooltip={true}
                                                        onDelete={() => handleRemoveAttachment(attachment.id)}
                                                    />
                                                </Grid2>
                                            ))}

                                        {!context?.graphEnabled && !context?.companyContextEnabled && !selectionExists && attachments.length === 0 && (
                                            <Typography fontSize={12} fontStyle={"italic"}>
                                                Keine Dokumente ausgewählt
                                            </Typography>
                                        )}
                                    </Grid2>

                                    {selectedContentNodes.length > 0 && !context?.graphEnabled && (
                                        <Grid2>
                                            <IconButton size={"small"} onClick={() => handleClearAll()}>
                                                <icon.close />
                                            </IconButton>
                                        </Grid2>
                                    )}
                                </Grid2>
                            </>
                        )}
                    </Grid2>
                </Card>

                {!isEffectiveGptMode && (
                    <Grid2 container size={12} px={1} alignItems={"center"}>
                        <Grid2 pr={0.5}>
                            <Typography fontSize={12}>Belegter Kontext</Typography>
                        </Grid2>

                        <Grid2>
                            <Tooltip
                                title={
                                    "Die Anzeige zeigt den noch verfügbaren Kontext pro Anfrage in %. \n\nHinterlegte Dokumente beeinflussen diesen Speicher – ist er voll, müssen Dokumente entfernt werden, um neue Anfragen zu stellen."
                                }
                            >
                                <IconButton size={"small"}>
                                    <icon.info sx={{ color: "semantic.icon.informative" }} />
                                </IconButton>
                            </Tooltip>
                        </Grid2>

                        <Grid2 size={"grow"} px={1}>
                            <LinearProgress
                                variant={"determinate"}
                                value={Math.min(progressPercentage, 100)} // Cap the visual display at 100%
                                color={!context?.graphEnabled ? (isOverflow ? "error" : "secondary") : undefined}
                                sx={
                                    context?.graphEnabled
                                        ? {
                                              "& .MuiLinearProgress-bar": {
                                                  backgroundColor: isOverflow ? "error.main" : "semantic.highlight.primary",
                                              },
                                          }
                                        : undefined
                                }
                            />
                        </Grid2>

                        <Grid2 px={1}>
                            <Typography fontSize={12}>{`${progressPercentage.toFixed(1)}%`}</Typography>
                        </Grid2>
                    </Grid2>
                )}
            </Card>

            <DocumentDialog
                open={!!selectedNode}
                onClose={() => setSelectedNode(null)}
                contentNode={selectedNode}
                documentReference={selectedNode?.associatedOfficialDocument?.reference}
            />
        </Grid2>
    );
});

export default SelectionBoard;
